import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import router from '@/router';
import { API_BASE_URL, API_PATHS } from '@/config/api';

// Globale Variable, um zu verfolgen, ob ein Token-Abruf gerade läuft
let csrfRequestInProgress = false;
let lastCSRFRequest = 0;
const MIN_REQUEST_INTERVAL = 5000; // 5 Sekunden Mindestabstand

// Hilfsfunktion zum Abrufen von Cookies
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
}

// Axios-Instanz erstellen
const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8000',
  withCredentials: true, // Wichtig für CSRF-Cookie
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

console.log('HTTP-Service initialisiert mit baseURL:', http.defaults.baseURL);

// CSRF-Token-Funktion
export async function fetchCSRFToken() {
  // Prüfen, ob eine Anfrage bereits läuft oder kürzlich durchgeführt wurde
  const now = Date.now();
  if (csrfRequestInProgress || (now - lastCSRFRequest < MIN_REQUEST_INTERVAL)) {
    console.log('CSRF-Anfrage wird übersprungen: Bereits im Gange oder kürzlich durchgeführt');
    return;
  }
  
  csrfRequestInProgress = true;
  lastCSRFRequest = now;
  
  try {
    // Laravel's sanctum/csrf-cookie endpoint verwenden
    await http.get('/sanctum/csrf-cookie');
    
    // Get the XSRF token from cookie
    const token = getCookie('XSRF-TOKEN');
    if (token) {
      http.defaults.headers.common['X-XSRF-TOKEN'] = token;
      console.log('CSRF-Token aktualisiert:', token);
    } else {
      console.warn('Kein CSRF-Token im Cookie gefunden');
      throw new Error('CSRF token not found in cookies');
    }
  } catch (error) {
    console.error('Fehler beim Abrufen des CSRF-Tokens:', error);
    throw error;
  } finally {
    csrfRequestInProgress = false;
  }
}

// Initialize CSRF token when creating http instance
(async () => {
  try {
    await fetchCSRFToken();
  } catch (error) {
    console.error('Initial CSRF token fetch failed:', error);
  }
})();

// Request-Interceptor
http.interceptors.request.use(async config => {
  // For non-GET requests, check CSRF token
  if (config.method !== 'get') {
    const token = getCookie('XSRF-TOKEN');
    if (token) {
      config.headers['X-XSRF-TOKEN'] = token;
    } else {
      await fetchCSRFToken();
      const newToken = getCookie('XSRF-TOKEN');
      if (newToken) {
        config.headers['X-XSRF-TOKEN'] = newToken;
      }
    }
  }

  // Auth-Token aus dem Store hinzufügen
  const authStore = useAuthStore();
  const token = authStore.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('HTTP Request wird mit Auth-Token gesendet:', { 
      url: config.url,
      method: config.method,
      hasToken: true
    });
  }
  
  return config;
}, error => {
  return Promise.reject(error);
});

// Response-Interceptor
http.interceptors.response.use(
  response => response,
  async error => {
    // Detailliertes Logging für Fehler
    console.error('HTTP-Fehler:', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.response?.data?.message || error.message
    });
    
    // CSRF Token Mismatch (419)
    if (error.response?.status === 419) {
      console.warn('CSRF Token Mismatch - Versuche Token neu zu holen');
      await fetchCSRFToken();
      // Retry the request with new token
      const newToken = getCookie('XSRF-TOKEN');
      if (newToken) {
        error.config.headers['X-XSRF-TOKEN'] = newToken;
        return http(error.config);
      }
    }
    
    // Rest of your error handling...
    if (error.response && error.response.status === 401) {
      const isLoginAttempt = error.config.url.includes(API_PATHS.LOGIN);
      
      if (!isLoginAttempt) {
        const errorMsg = error.response.data.message || 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
        console.warn('Authentifizierungsfehler (401):', errorMsg);
        
        const authStore = useAuthStore();
        await authStore.logout();
        router.push('/login');
      }
    }
    
    if (error.response && error.response.status === 403) {
      const errorMsg = error.response.data.message || 'Sie haben keine Berechtigung für diese Aktion.';
      console.warn('Berechtigungsfehler (403):', errorMsg, 'URL:', error.config.url);
      
      if (error.config.url.includes(API_PATHS.ADMIN_COMPANIES)) {
        console.warn('Normaler Benutzer versucht, auf Admin-API zuzugreifen. Verwende stattdessen /api/company');
        
        const authStore = useAuthStore();
        const user = authStore.user;
        if (user && user.role !== 'super_admin') {
          console.info('Versuche alternativen API-Aufruf für normalen Benutzer...');
          
          try {
            const newUrl = API_PATHS.COMPANY;
            const newConfig = { ...error.config, url: newUrl };
            delete newConfig.baseURL;
            
            return http(newConfig);
          } catch (retryError) {
            console.error('Auch alternativer API-Aufruf fehlgeschlagen:', retryError);
          }
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default http;